/* Rynoka: toyland-mega-menu.js — progressive enhancement for the desktop mega-menu.
   The reveal itself is pure CSS (:hover / :focus-within), so the menu works with JS off.
   This layer only adds: aria-expanded sync, hover-intent (anti-flicker), and Escape-to-close.
   Event-delegated — no per-node listeners (CLAUDE.md rule 7). */
(function () {
  'use strict';

  var OPEN_DELAY = 90;   // ms — lets diagonal mouse travel cross a neighbour without opening it
  var CLOSE_DELAY = 180; // ms — lets the pointer cross the gap from trigger to panel

  var openTimer = null;
  var closeTimer = null;
  var current = null; // the .tl-mega element currently marked open

  // Matches .tl-header__nav (visible >900px) so the mega opens wherever the
  // horizontal nav is shown — no dead zone between 901px and 1023px.
  var DESKTOP = '(min-width: 901px)';

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  var VIEWPORT_MARGIN = 12; // px kept between the panel and the viewport edge

  // The panel opens left-aligned under its trigger (transform-origin: top left).
  // For right-most departments its natural right edge can spill past the viewport,
  // which would clip it (and risk a horizontal scrollbar). Measure and hand the CSS
  // a --tl-mega-shift so it slides just enough left to stay on screen.
  function positionPanel(mega) {
    var panel = mega.querySelector('[data-tl-mega-panel]');
    if (!panel) return;
    panel.style.setProperty('--tl-mega-shift', '0px');
    var rect = panel.getBoundingClientRect();
    // Use the untransformed width so the near-1 open-state scale doesn't skew the math.
    var w = panel.offsetWidth;
    var left = rect.left;
    var right = left + w;
    var vw = document.documentElement.clientWidth;
    var shift = 0;
    if (right > vw - VIEWPORT_MARGIN) {
      shift = (vw - VIEWPORT_MARGIN) - right; // negative → slide left
    }
    // Never push the left edge off the other side of the screen.
    if (left + shift < VIEWPORT_MARGIN) {
      shift = VIEWPORT_MARGIN - left;
    }
    if (shift) panel.style.setProperty('--tl-mega-shift', Math.round(shift) + 'px');
  }

  function setExpanded(mega, expanded) {
    if (!mega) return;
    var trigger = mega.querySelector('[data-tl-mega-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    mega.classList.toggle('is-open', expanded);
  }

  function open(mega) {
    if (current === mega) return;
    if (current) setExpanded(current, false);
    current = mega;
    positionPanel(mega);
    setExpanded(mega, true);
  }

  function close(mega) {
    if (!mega) return;
    setExpanded(mega, false);
    if (current === mega) current = null;
  }

  function closeAll() {
    var open = document.querySelectorAll('[data-tl-mega].is-open');
    for (var i = 0; i < open.length; i++) setExpanded(open[i], false);
    current = null;
  }

  function clearTimers() {
    if (openTimer) { clearTimeout(openTimer); openTimer = null; }
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
  }

  // ── Hover intent ────────────────────────────────────────────────
  var hovered = null; // mega the pointer is currently within (for one-shot positioning)

  document.addEventListener('mouseover', function (e) {
    if (!isDesktop()) return;
    var mega = e.target.closest ? e.target.closest('[data-tl-mega]') : null;
    if (!mega) return;
    // The panel reveals instantly via CSS :hover, so correct its position on the
    // first entry — not in the delayed open(), which may never fire while the
    // pointer keeps moving. Guard so we only measure once per entry (no thrash).
    if (mega !== hovered) {
      hovered = mega;
      positionPanel(mega);
    }
    clearTimers();
    openTimer = setTimeout(function () { open(mega); }, OPEN_DELAY);
  });

  document.addEventListener('mouseout', function (e) {
    if (!isDesktop()) return;
    var mega = e.target.closest ? e.target.closest('[data-tl-mega]') : null;
    if (!mega) return;
    // Ignore moves that stay inside the same mega (trigger → panel).
    if (e.relatedTarget && mega.contains(e.relatedTarget)) return;
    if (hovered === mega) hovered = null;
    clearTimers();
    closeTimer = setTimeout(function () { close(mega); }, CLOSE_DELAY);
  });

  // ── Keyboard: focus opens, Escape closes and restores focus ──────
  document.addEventListener('focusin', function (e) {
    if (!isDesktop()) return;
    var mega = e.target.closest ? e.target.closest('[data-tl-mega]') : null;
    clearTimers();
    if (mega) {
      open(mega);
    } else if (current) {
      // Focus left the menu entirely.
      close(current);
    }
  });

  document.addEventListener('keydown', function (e) {
    var mega = e.target.closest ? e.target.closest('[data-tl-mega]') : null;
    if (!mega) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      clearTimers();
      close(mega);
      var trigger = mega.querySelector('[data-tl-mega-trigger]');
      if (trigger) trigger.focus();
    }
  });

  // Touch devices get the accordion drawer instead — never trap the tap.
  document.addEventListener('touchstart', function () {
    clearTimers();
    closeAll();
  }, { passive: true });

  // Keep an open panel inside the viewport as the window resizes.
  window.addEventListener('resize', function () {
    if (current && isDesktop()) positionPanel(current);
  });

  // Leaving the viewport / navigating away shouldn't leave a panel stuck open.
  window.addEventListener('pagehide', closeAll);
  window.matchMedia(DESKTOP).addEventListener('change', function () {
    clearTimers();
    closeAll();
  });
})();
